import { ApiService } from 'js/src/client';
import { ErrorReporter } from 'js/src/libs/error-reporter';

type AccumulatedData = { [eventName: string]: { [key: string]: number } };

export class BatchAnalytics {
    private static accumulatedData: AccumulatedData = {};
    private static intervalId: number | null = null;
    private static INTERVAL_DURATION = 10000;

    static {
        this.addEventListeners();
    }

    public static reset(): void {
        this.accumulatedData = {};
        this.intervalId = null;
    }

    private static addEventListeners(): void {
        window.addEventListener('beforeunload', () => {
            if (this.intervalId !== null) {
                window.clearInterval(this.intervalId);
                this.intervalId = null;
            }
            this.flush();
        });
    }

    private static flush(): void {
        const events: {
            event_name: string;
            properties: { [k: string]: unknown };
        }[] = Object.keys(this.accumulatedData).map((key) => ({
            event_name: key,
            properties: this.accumulatedData[key],
        }));
        if (events.length === 0) {
            return;
        }
        ApiService.mixyCreate({
            body: {
                people_set: analytics?._people_set ?? {},
                people_set_once: analytics?._people_set_once ?? {},
                super_properties: analytics?._super_properties ?? {},
                events,
            },
        })
            .then(() => {
                this.accumulatedData = {};
            })
            .catch((error: unknown) => {
                /*
                 * If the request fails, we do not want to keep spamming the server with requests.
                 * We will clear the accumulated data so that it is not sent again.
                 */
                ErrorReporter.capture(error);
                this.accumulatedData = {};
            });
    }

    public static track(
        eventName: string,
        fixedData: { [k: string]: number },
        counterData: { [k: string]: number } = {},
    ): void {
        if (!Object.prototype.hasOwnProperty.call(this.accumulatedData, eventName)) {
            this.accumulatedData[eventName] = {};
        }

        for (const key in fixedData) {
            if (Object.prototype.hasOwnProperty.call(fixedData, key)) {
                this.accumulatedData[eventName][key] = fixedData[key];
            }
        }

        for (const key in counterData) {
            if (Object.prototype.hasOwnProperty.call(counterData, key)) {
                if (!Object.prototype.hasOwnProperty.call(this.accumulatedData[eventName], key)) {
                    this.accumulatedData[eventName][key] = 0;
                }
                this.accumulatedData[eventName][key] += counterData[key];
            }
        }

        if (this.intervalId === null) {
            this.intervalId = window.setInterval(() => {
                this.flush();
            }, this.INTERVAL_DURATION);
        }
    }
}
